import React, { useEffect, useRef, useState } from 'react';
import type { SurveyQuestion, SurveyQuestionType } from '../../../types/venue';
import Button from '../../ui/Button';

export interface SurveyData {
  enabled: boolean;
  questions: SurveyQuestion[];
}

interface SurveySetupStepProps {
  data: SurveyData;
  onUpdate: (data: SurveyData) => void;
  onNext: () => void;
  onBack: () => void;
}

const QUESTION_TYPES: { value: SurveyQuestionType; label: string }[] = [
  { value: 'star_rating', label: 'Star rating (1–5)' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'free_text', label: 'Free text' },
];

const TEMPLATES = [
  {
    label: 'Quick feedback',
    questions: [
      { id: 'tpl-1', type: 'star_rating' as SurveyQuestionType, text: 'How would you rate your experience today?' },
    ],
  },
  {
    label: 'Discovery',
    questions: [
      { id: 'tpl-2', type: 'multiple_choice' as SurveyQuestionType, text: 'How did you hear about us?', options: ['Social Media', 'Friend', 'Walk-in', 'Google', 'Other'] },
    ],
  },
  {
    label: 'Full survey',
    questions: [
      { id: 'tpl-3', type: 'star_rating' as SurveyQuestionType, text: 'How would you rate your experience today?' },
      { id: 'tpl-4', type: 'multiple_choice' as SurveyQuestionType, text: 'How did you hear about us?', options: ['Social Media', 'Friend', 'Walk-in', 'Google', 'Other'] },
    ],
  },
];

const inputBase =
  'w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const SurveySetupStep: React.FC<SurveySetupStepProps> = ({ data, onUpdate, onNext, onBack }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const optionInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const focusOptionKey = useRef<string | null>(null);

  // Move focus into an option input that was just added via the Enter key.
  useEffect(() => {
    if (!focusOptionKey.current) return;
    optionInputs.current[focusOptionKey.current]?.focus();
    focusOptionKey.current = null;
  });

  const toggleEnabled = () => {
    onUpdate({ ...data, enabled: !data.enabled });
  };

  const addQuestion = () => {
    if (data.questions.length >= 2) return;
    const newQ: SurveyQuestion = {
      id: `q-${Date.now()}`,
      type: 'star_rating',
      text: '',
    };
    onUpdate({ ...data, questions: [...data.questions, newQ] });
  };

  const updateQuestion = (index: number, updates: Partial<SurveyQuestion>) => {
    const updated = data.questions.map((q, i) =>
      i === index ? { ...q, ...updates } : q
    );
    onUpdate({ ...data, questions: updated });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`q-${index}`];
      return next;
    });
  };

  const removeQuestion = (index: number) => {
    onUpdate({ ...data, questions: data.questions.filter((_, i) => i !== index) });
  };

  const applyTemplate = (template: typeof TEMPLATES[0]) => {
    onUpdate({ ...data, enabled: true, questions: template.questions });
  };

  const addOption = (qIndex: number) => {
    const q = data.questions[qIndex];
    if ((q.options?.length || 0) >= 5) return;
    updateQuestion(qIndex, { options: [...(q.options || []), ''] });
  };

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    const q = data.questions[qIndex];
    const opts = [...(q.options || [])];
    opts[optIndex] = value;
    updateQuestion(qIndex, { options: opts });
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, qIndex: number) => {
    if (e.key !== 'Enter') return;
    // Never let Enter submit or advance the wizard step.
    e.preventDefault();
    const count = data.questions[qIndex].options?.length || 0;
    if (count >= 5) return;
    focusOptionKey.current = `${qIndex}-${count}`;
    addOption(qIndex);
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    const q = data.questions[qIndex];
    const opts = (q.options || []).filter((_, i) => i !== optIndex);
    updateQuestion(qIndex, { options: opts });
  };

  const validate = (): boolean => {
    if (!data.enabled) return true;
    const newErrors: Record<string, string> = {};
    data.questions.forEach((q, i) => {
      if (!q.text.trim()) newErrors[`q-${i}`] = 'Question text is required';
      if (q.type === 'multiple_choice' && (!q.options || q.options.filter(o => o.trim()).length < 2)) {
        newErrors[`q-${i}-opts`] = 'At least 2 options are required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Step 4 of 5</div>
        <h2 className="text-display text-2xl text-coffee-900">Survey setup</h2>
        <p className="mt-1.5 text-sm text-coffee-600">
          Ask guests a quick question before revealing your Wi-Fi password. Max 2 questions.
        </p>
      </div>

      {/* Enable toggle */}
      <label className="flex items-center gap-3 p-4 rounded-xl border border-coffee-100 bg-white cursor-pointer hover:border-coffee-200 transition-colors">
        <div
          role="switch"
          aria-checked={data.enabled}
          tabIndex={0}
          onClick={toggleEnabled}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEnabled(); } }}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            data.enabled ? 'bg-coffee-600' : 'bg-coffee-200'
          }`}
        >
          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
            data.enabled ? 'translate-x-5' : ''
          }`} />
        </div>
        <div>
          <span className="text-sm font-medium text-coffee-900">Enable survey</span>
          <p className="text-xs text-coffee-500 mt-0.5">
            Guests answer your questions before getting Wi-Fi access.
          </p>
        </div>
      </label>

      {data.enabled && (
        <>
          {/* Templates */}
          <div>
            <p className="text-xs tracking-eyebrow font-semibold text-coffee-500 mb-2">Quick templates</p>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  onClick={() => applyTemplate(tpl)}
                  className="px-3 py-1.5 text-xs font-medium bg-coffee-50 border border-coffee-100 text-coffee-700 rounded-lg hover:bg-coffee-100 hover:border-coffee-200 transition-colors"
                >
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-3">
            {data.questions.map((question, qIndex) => (
              <div key={question.id} className="p-4 border border-coffee-100 rounded-xl bg-white">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs tracking-eyebrow font-semibold text-coffee-500">
                    Question {qIndex + 1}
                  </span>
                  <button
                    onClick={() => removeQuestion(qIndex)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                    aria-label={`Remove question ${qIndex + 1}`}
                  >
                    Remove
                  </button>
                </div>

                <select
                  value={question.type}
                  onChange={(e) => updateQuestion(qIndex, {
                    type: e.target.value as SurveyQuestionType,
                    options: e.target.value === 'multiple_choice' ? ['', ''] : undefined,
                  })}
                  className={`${inputBase} mb-3`}
                  aria-label="Question type"
                >
                  {QUESTION_TYPES.map((qt) => (
                    <option key={qt.value} value={qt.value}>{qt.label}</option>
                  ))}
                </select>

                <input
                  type="text"
                  value={question.text}
                  onChange={(e) => updateQuestion(qIndex, { text: e.target.value })}
                  placeholder="Enter your question..."
                  className={`${inputBase} ${errors[`q-${qIndex}`] ? 'border-red-400' : ''}`}
                  aria-label="Question text"
                />
                {errors[`q-${qIndex}`] && (
                  <p className="text-red-500 text-xs mt-1">{errors[`q-${qIndex}`]}</p>
                )}

                {question.type === 'multiple_choice' && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-coffee-600">Options (max 5)</p>
                    {(question.options || []).map((opt, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          ref={(el) => { optionInputs.current[`${qIndex}-${optIndex}`] = el; }}
                          onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                          onKeyDown={(e) => handleOptionKeyDown(e, qIndex)}
                          placeholder={`Option ${optIndex + 1}`}
                          className="flex-1 px-3 py-2 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500"
                          aria-label={`Option ${optIndex + 1}`}
                        />
                        {(question.options?.length || 0) > 2 && (
                          <button
                            onClick={() => removeOption(qIndex, optIndex)}
                            className="text-xs text-red-500 hover:text-red-700 px-1 font-medium"
                            aria-label={`Remove option ${optIndex + 1}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {(question.options?.length || 0) < 5 && (
                      <button
                        onClick={() => addOption(qIndex)}
                        className="text-xs text-coffee-600 hover:text-coffee-800 font-medium"
                      >
                        + Add option
                      </button>
                    )}
                    {errors[`q-${qIndex}-opts`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`q-${qIndex}-opts`]}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {data.questions.length < 2 && (
              <button
                onClick={addQuestion}
                className="w-full py-3 border-2 border-dashed border-coffee-200 rounded-xl text-sm text-coffee-500 hover:border-coffee-300 hover:text-coffee-700 transition-colors"
              >
                + Add question ({data.questions.length}/2)
              </button>
            )}
          </div>
        </>
      )}

      <div className="flex justify-between pt-2 border-t border-coffee-100">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <div className="flex gap-2">
          {data.enabled && data.questions.length === 0 && (
            <Button variant="ghost" onClick={onNext}>Skip</Button>
          )}
          <Button variant="primary" onClick={handleNext}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default SurveySetupStep;
